import React, { useState, useEffect, useRef, useCallback } from 'react';
import IconButton from './IconButton';
import InputText from './InputText';
import InputTextArea from './InputTextArea';
import SaveStatusFeedback, { SaveStatus } from './SaveStatusFeedback';
import MenuButton from './MenuButton';
import { TodoTagEntity } from '../types/data';
import './DrawerTagDetail.css';

interface DrawerTagDetailProps {
  /** 表示するタグ項目 */
  tag?: TodoTagEntity;
  /** ドロワーの表示状態 */
  isOpen: boolean;
  /** ドロワーを閉じる時のコールバック */
  onClose: () => void;
  /** タグ保存時のコールバック */
  onSave?: (tag: TodoTagEntity) => void;
  /** 新規タグ作成時のコールバック */
  onCreate?: (tag: Omit<TodoTagEntity, 'id'>) => void;
  /** タグ削除時のコールバック */
  onDelete?: (tagId: string) => void;
}

const DrawerTagDetail: React.FC<DrawerTagDetailProps> = ({
  tag,
  isOpen,
  onClose,
  onSave,
  onCreate,
  onDelete
}) => {
  // ローカル状態管理（UI即座更新用）
  const [localTag, setLocalTag] = useState<TodoTagEntity | null>(null);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const [saveError, setSaveError] = useState<string | undefined>();
  const [isCreated, setIsCreated] = useState(false); // 新規作成済みフラグ

  // 新規作成かどうかの判定
  const isNewTag = tag?.id === '' && !isCreated;

  // 新規タグが入力されているかどうかの判定
  const hasNewTagInput = useCallback((tag: TodoTagEntity) => {
    return tag.title.trim() !== '';
  }, []);

  // DOM参照
  const drawerRef = useRef<HTMLDivElement>(null);

  // デバウンスタイマーの参照
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  // 成功メッセージ自動削除タイマーの参照
  const successTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // 成功メッセージの自動削除
  const clearSuccessMessage = useCallback(() => {
    if (successTimeoutRef.current) {
      clearTimeout(successTimeoutRef.current);
    }
    successTimeoutRef.current = setTimeout(() => {
      setSaveStatus('idle');
    }, 2000);
  }, []);

  const isCreatingRef = useRef(false); // 新規作成の同時実行を防ぐためのフラグ
  const isComposingRef = useRef(false); // IME変換中を同期的に判定するためのref
  const prevTagIdRef = useRef<string | null>(null); // 前回同期した Tag の ID

  // 実際の保存処理（成功・失敗のハンドリング付き）
  const performSave = useCallback(async (tag: TodoTagEntity) => {
    try {
      setSaveStatus('saving');
      setSaveError(undefined);

      // 日本語入力の変換中は新規作成を行わない
      if (isNewTag && isComposingRef.current) {
        setSaveStatus('dirty');
        return;
      }

      if (isNewTag) {
        // タイトルが入力されていなければ新規作成しない
        if (!hasNewTagInput(tag)) {
          setSaveStatus('idle');
          return;
        }

        // 新規作成の同時実行（二重発火）を防止
        if (isCreatingRef.current) {
          return;
        }
        isCreatingRef.current = true;

        try {
          // id を除いた形で onCreate を呼ぶ
          const { id, ...tagWithoutId } = tag;
          await Promise.resolve(onCreate?.(tagWithoutId));
          setIsCreated(true); // 作成済みフラグを設定（以後は更新扱い）
        } finally {
          isCreatingRef.current = false;
        }
      } else {
        // 既存タグの更新
        await Promise.resolve(onSave?.(tag));
      }

      setSaveStatus('success');
      clearSuccessMessage();
    } catch (error) {
      setSaveStatus('error');
      setSaveError(error instanceof Error ? error.message : '保存に失敗しました');
    }
  }, [isNewTag, hasNewTagInput, onCreate, onSave, clearSuccessMessage]);

  // デバウンス付き保存処理
  const debouncedSave = useCallback((tag: TodoTagEntity) => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    setSaveStatus('dirty');

    debounceTimerRef.current = setTimeout(() => {
      performSave(tag);
    }, 500);
  }, [performSave]);

  // propsからlocalTagへの同期
  useEffect(() => {
    const currentTagId = tag?.id ?? null;
    const hasTagChanged = currentTagId !== prevTagIdRef.current;

    if (hasTagChanged || !localTag) {
      if (tag) {
        setLocalTag({ ...tag });
        setIsCreated(tag.id !== '');
        prevTagIdRef.current = currentTagId;
      } else {
        setLocalTag(null);
        setIsCreated(false);
        prevTagIdRef.current = null;
      }
      setSaveStatus('idle');
      setSaveError(undefined);
    }
  }, [tag, localTag]);

  // クリーンアップ
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      if (successTimeoutRef.current) {
        clearTimeout(successTimeoutRef.current);
      }
    };
  }, []);

  // ドロワーが開いた時にスクロール位置をリセット
  useEffect(() => {
    if (isOpen && drawerRef.current) {
      drawerRef.current.scrollTop = 0;
    }
  }, [isOpen]);

  // フィールド更新ハンドラー
  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!localTag) return;
    const updatedTag: TodoTagEntity = {
      ...localTag,
      title: e.target.value,
      modified_at: new Date().toISOString()
    };
    setLocalTag(updatedTag);
    debouncedSave(updatedTag);
  };

  const handleBodyChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (!localTag) return;
    const updatedTag: TodoTagEntity = {
      ...localTag,
      body: e.target.value,
      modified_at: new Date().toISOString()
    };
    setLocalTag(updatedTag);
    debouncedSave(updatedTag);
  };

  // IME入力のハンドリング
  const handleCompositionStart = () => {
    isComposingRef.current = true;
  };

  const handleCompositionEnd = () => {
    isComposingRef.current = false;
  };

  // タグ削除
  const handleDelete = () => {
    if (!localTag || isNewTag) return;
    onDelete?.(localTag.id);
    // onCloseは親コンポーネント側で呼ばれるため、ここでは呼ばない
  };

  return (
    <div className={`drawer-tag-detail ${isOpen ? 'drawer-tag-detail--open' : ''}`}>
      {/* ドロワー本体 */}
      <div
        ref={drawerRef}
        className="drawer-tag-detail__drawer"
        role="dialog"
        aria-modal="true"
        aria-labelledby="drawer-tag-title"
      >
        {localTag && (
          <>
            {/* ヘッダー */}
            <div className="drawer-tag-detail__header">
              <div className="drawer-tag-detail__header-left">
                <IconButton
                  icon="close"
                  onClick={onClose}
                  aria-label="閉じる"
                  tooltip="閉じる"
                  tooltipPosition="bottom-right"
                />
              </div>
              <div className="drawer-tag-detail__header-center">
                <SaveStatusFeedback
                  status={saveStatus}
                  errorMessage={saveError}
                />
              </div>
              <div className="drawer-tag-detail__header-right">
                {!isNewTag && (
                  <MenuButton
                    icon="menu"
                    aria-label="タグ操作メニュー"
                    tooltip="タグ操作メニュー"
                    tooltipPosition="bottom-left"
                    items={[
                      {
                        type: 'Destructive',
                        label: 'タグを削除',
                        leftIcon: 'delete',
                        showLeftIcon: true,
                        onClick: handleDelete
                      }
                    ]}
                  />
                )}
              </div>
            </div>

            {/* コンテンツ */}
            <div className="drawer-tag-detail__content">
              {/* タイトル入力 */}
              <div className="drawer-tag-detail__field">
                <InputText
                  hasLeftIcon={true}
                  leftIcon="tag"
                  placeholder="タグ名を入力"
                  value={localTag.title}
                  onChange={handleTitleChange}
                  onCompositionStart={handleCompositionStart}
                  onCompositionEnd={handleCompositionEnd}
                  autoFocus={isNewTag}
                />
              </div>

              {/* 本文入力 */}
              <div className="drawer-tag-detail__field">
                <InputTextArea
                  placeholder="説明を入力（任意）"
                  value={localTag.body || ''}
                  onChange={handleBodyChange}
                  onCompositionStart={handleCompositionStart}
                  onCompositionEnd={handleCompositionEnd}
                  rows={6}
                />
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default DrawerTagDetail;
